/* ==========================================================================
   Bookings -- the list (cleaner side) and the detail (both sides)
   ========================================================================== */

import { useState } from 'react';
import type { Booking, Request, Role } from '../../types';
import {
  Avatar, Card, Divider, Empty, JobChips, Note, SectionHead, Seg, StatusTrack, Stars,
  Switch,
} from '../../components/ui';
import { BOOKINGS, REQUESTS } from '../../data/seed';
import { byId, cleaner, house, jobLabel, payLabel, shortName, homeTypeLabel, townLabel } from '../../lib/domain';
import { TODAY_ISO, endTime, fmtDate, fmtTime, money } from '../../lib/time';
import { t, tf } from '../../lib/i18n';
import {
  activeCleaner, cancelBooking, commit, completeBooking, nav, openChat, openSheet, rebook, setPaid,
  startBooking, state, tab,
} from '../../store';
import { PhotoStrip } from '../../components/Photos';
import { PushNudge } from '../../components/PushCard';

/* --------------------------------------------------------------- the card */

export function BookingCard({ b, side }: { b: Booking; side: Role }) {
  const other = side === 'cleaner' ? house(b.houseId) : cleaner(b.cleanerId);
  const otherName = side === 'cleaner' ? house(b.houseId).displayName : cleaner(b.cleanerId).name;
  const mine = side === 'cleaner' ? b.reviewByCleaner : b.reviewByHouse;
  const needsReview = b.status === 'completed' && !mine;

  return (
    <Card
      recur={!!b.recurringId}
      onClick={() => nav(side === 'cleaner' ? 'c_booking' : 'h_booking', { id: b.id })}
    >
      <div className="row top">
        <Avatar p={other} />
        <div className="col grow">
          <div className="row between">
            <b className="name">{otherName}</b>
            <span className="rate">{money(b.rate * b.hours)}</span>
          </div>
          <span className="sub">
            {fmtDate(b.date, { long: true })} · {fmtTime(b.start)}–{endTime(b.start, b.hours)}
          </span>
          <span className="sub tight">
            {side === 'cleaner'
              ? townLabel(house(b.houseId))
              /* A home with two properties needs to know which one -- the
                 cleaner's side already said "the rental on Elm" while this
                 side only named the cleaner. */
              : (state.me?.houseIds.length ?? 0) > 1
                ? house(b.houseId).displayName
                : cleaner(b.cleanerId).headline}
          </span>
        </div>
      </div>
      <div className="row between">
        <span className={'chip' + (b.status === 'confirmed' ? ' good' : '')}>
          {b.status === 'confirmed' ? t('tx_confirmed') : b.status === 'cancelled' ? t('tx_cancelled') : t('finished')}
        </span>
        {needsReview && <span className="chip warm">{t('b_reviewwaiting')}</span>}
        {b.status === 'completed' && (
          <span className={'chip' + (b.paidAt ? ' good' : ' warm')}>
            {b.paidAt ? t('tx_paid') : t('tx_not_paid')}
          </span>
        )}
      </div>
    </Card>
  );
}

/* --------------------------------------------------------------- the list */

export function CleanerBookings() {
  const c = activeCleaner();
  /* Cleaner-only screen. A home reaching it means a bad link, not a crash. */
  if (!c) return <div className="screen-pad"><Empty big={t('c_cleaneronly')} /></div>;

  const mine = BOOKINGS.filter((b) => b.cleanerId === c.id);
  const confirmed = mine.filter((b) => b.status === 'confirmed');
  const upcoming = confirmed
    .filter((b) => b.date >= TODAY_ISO)
    .sort((a, b) => a.date.localeCompare(b.date));
  /* The day came and went and nobody said what happened. Not upcoming, not
     finished: its own pile, at the top, until it is dealt with. */
  const overdue = confirmed
    .filter((b) => b.date < TODAY_ISO)
    .sort((a, b) => b.date.localeCompare(a.date));
  const past = mine.filter((b) => b.status === 'completed');
  const earned = past.reduce((s, b) => s + b.rate * b.hours, 0);
  const booked = upcoming.reduce((s, b) => s + b.rate * b.hours, 0);

  /* Everything still waiting on a home's answer. Without this a cleaner has no
     idea what they are owed a reply on. */
  const applied = REQUESTS.filter(
    (r) => r.status === 'open' && r.applicants.some((a) => a.cleanerId === c.id),
  ).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="screen-pad">
      <PushNudge />
      <Card className="tint">
        <div className="row between">
          <div className="col">
            <span className="sub tight">{t('b_earned')}</span>
            <span className="rate" style={{ fontSize: 24 }}>{money(earned)}</span>
          </div>
          <div className="col" style={{ textAlign: 'right' }}>
            <span className="sub tight">{t('b_comingup')}</span>
            <span className="rate" style={{ fontSize: 24 }}>{money(booked)}</span>
          </div>
        </div>
        <span className="sub tight">
          {t('b_neverholds')}
        </span>
      </Card>

      <Seg
        options={[
          { id: 'upcoming', label: t('upcoming') },
          { id: 'applied', label: `${t('applied_tab')}${applied.length ? ' · ' + applied.length : ''}` },
          { id: 'finished', label: t('finished') },
        ]}
        value={state.jobsMode}
        onChange={(v) => { state.jobsMode = v; commit(); }}
      />

      {state.jobsMode === 'upcoming' && overdue.length > 0 && (
        <>
          <SectionHead title={t('h_needsfinishing')} />
          <Note warm>{t('n_needsfinishing_c')}</Note>
          <div className="cardgrid">
            {overdue.map((b) => <BookingCard key={b.id} b={b} side="cleaner" />)}
          </div>
          <SectionHead title={t('upcoming')} />
        </>
      )}

      {state.jobsMode === 'upcoming' && (
        upcoming.length
          ? (
              <div className="cardgrid">
                {upcoming.map((b) => <BookingCard key={b.id} b={b} side="cleaner" />)}
              </div>
            )
          : <Empty
              big={t('nothing')}
              sub={t('b_nothingbooked')}
              action={t('t_findwork')}
              onAction={() => tab('c_jobs')}
            />
      )}

      {state.jobsMode === 'applied' && (
        applied.length
          ? (
              <div className="cardgrid">
                {applied.map((r) => <AppliedCard key={r.id} r={r} cleanerId={c.id} />)}
              </div>
            )
          : <Empty
              big={t('b_nothingwaiting')}
              sub={t('b_appliedhere')}
              action={t('t_findwork')}
              onAction={() => tab('c_jobs')}
            />
      )}

      {state.jobsMode === 'finished' && (
        past.length
          ? <>
              <Owed jobs={past} side="cleaner" />
              <div className="cardgrid">
                {past.map((b) => <BookingCard key={b.id} b={b} side="cleaner" />)}
              </div>
            </>
          : <Empty big={t('nothing')} sub={t('r_nofinished')} />
      )}
    </div>
  );
}

/** What is still outstanding. Only appears when something actually is.

    Ozeret never sees the money, so this is only as good as what people tick.
    That is the point: it is a shared memory, not an account. */
export function Owed({ jobs, side }: { jobs: Booking[]; side: Role }) {
  const unpaid = jobs.filter((b) => !b.paidAt);
  if (!unpaid.length) return null;
  const sum = unpaid.reduce((s, b) => s + b.rate * b.hours, 0);

  return (
    <Card className="tint">
      <div className="row between">
        <div className="col">
          <span className="sub tight">
            {side === 'cleaner' ? t('tx_not_marked_paid') : t('tx_you_not_marked_paid')}
          </span>
          <b style={{ fontSize: 15 }}>
            {tf(unpaid.length === 1 ? 'tx_jobs_1' : 'tx_jobs_n', { n: unpaid.length })}
          </b>
        </div>
        <span className="rate" style={{ fontSize: 22 }}>{money(sum)}</span>
      </div>
    </Card>
  );
}

/** A job you are waiting to hear back on. */
function AppliedCard({ r, cleanerId }: { r: Request; cleanerId: string }) {
  const h = house(r.houseId);
  const mine = r.applicants.find((a) => a.cleanerId === cleanerId)!;
  const others = r.applicants.filter(
    (a) => a.status !== 'declined' && a.cleanerId !== cleanerId,
  ).length;

  return (
    <Card recur={!!r.recurring} onClick={() => nav('c_job', { id: r.id })}>
      <div className="row top">
        <Avatar p={h} />
        <div className="col grow">
          <div className="row between">
            <b className="name">{h.displayName}</b>
            <span className="rate">
              {money(mine.counterRate ?? r.rate)}<small>{t('perhour')}</small>
            </span>
          </div>
          <span className="sub">
            {fmtDate(r.date, { long: true })} · {fmtTime(r.start)}–{endTime(r.start, r.hours)}
          </span>
          <span className="sub tight">{townLabel(h)}</span>
        </div>
      </div>
      <div className="chips"><JobChips ids={r.jobIds} max={3} /></div>
      <div className="row between">
        <span className={'chip' + (mine.counterRate ? ' warm' : '')}>
          {mine.status === 'invited'
            ? t('tx_they_invited')
            : mine.counterRate
              ? tf('tx_you_countered', { rate: money(mine.counterRate) })
              : t('tx_waiting_back')}
        </span>
        <span className="sub tight">
          {others ? tf(others > 1 ? 'tx_others_applied_n' : 'tx_others_applied_1', { n: others }) : t('tx_only_you')}
        </span>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------- the detail */

export function BookingDetail({ side }: { side: Role }) {
  const b = byId(BOOKINGS, state.params.id!)!;
  const h = house(b.houseId);
  const c = cleaner(b.cleanerId);
  const other = side === 'cleaner' ? h : c;
  const otherName = side === 'cleaner' ? h.displayName : c.name;
  const myReview = side === 'cleaner' ? b.reviewByCleaner : b.reviewByHouse;
  const theirReview = side === 'cleaner' ? b.reviewByHouse : b.reviewByCleaner;
  /* Posted, Chosen, Underway, Done, Reviewed. */
  const step = b.status === 'completed'
    ? (myReview ? 4 : 3)
    : b.startedAt ? 2 : 1;
  const doneCount = b.jobState.filter((j) => j.done).length;
  const [confirmCancel, setConfirmCancel] = useState(false);
  const notesBack = b.jobState.filter((j) => j.note);

  return (
    <div className="screen-pad">
      <Card><StatusTrack step={step} /></Card>

      <Card>
        <div className="row top">
          <Avatar p={other} size="lg" />
          <div className="col grow">
            <b className="name" style={{ fontSize: 18 }}>{otherName}</b>
            <span className="sub">
              {side === 'cleaner' ? [homeTypeLabel(h.homeType), townLabel(h)].filter(Boolean).join(' · ') : c.headline}
            </span>
            <div className="row" style={{ gap: 6, marginTop: 4 }}>
              <Stars value={side === 'cleaner' ? h.ratings.overall : c.ratings.overall} />
            </div>
          </div>
        </div>
        <Divider />
        <div className="row between">
          <span className="sub">{t('when')}</span>
          <b>
            {fmtDate(b.date, { long: true })}, {fmtTime(b.start)}–{endTime(b.start, b.hours)}
          </b>
        </div>
        <div className="row between">
          <span className="sub">{t('b_agreedrate')}</span>
          <span className="rate">
            {money(b.rate)}<small>{t('perhour')} × {b.hours}h</small>
          </span>
        </div>
        <div className="row between">
          <span className="sub">{t('total')}</span>
          <span className="rate">{money(b.rate * b.hours)}</span>
        </div>
        <div className="row between">
          <span className="sub">{t('b_paidby')}</span>
          <b>{payLabel(b.payMethod)}</b>
        </div>
        {b.status === 'confirmed' && (
          <Note>
            <b>{t('tx_address')}</b>
            <br />
            {h.address
              ? h.address
              : side === 'cleaner'
                ? 'They have not filled in an address yet — ask them in the chat.'
                : 'You have not set an address. Add it in Edit this home so cleaners know where to come.'}
          </Note>
        )}
        {b.loggedAfterTheFact && (
          <Note warm>{t('b_afterfact')}</Note>
        )}
        {b.onceNote && (
          <Note warm>
            <b>{t('b_justthistime')}</b>
            <br />
            {b.onceNote}
          </Note>
        )}

        {/* The home can add here; the cleaner can only look. */}
        <PhotoStrip
          kind="booking"
          ownerId={b.id}
          canAdd={side === 'house'}
          label={side === 'house' ? 'Pictures for this visit' : 'Pictures from the home'}
        />

        {/* Ozeret never holds the money, so it cannot know. Both sides tick the
            same box, which is what makes it useful: "who have I not paid?" */}
        {b.status === 'completed' && (
          <>
            <Divider />
            <div className="switchrow">
              <div className="col">
                <b style={{ fontSize: 14 }}>{t('tx_paid')}</b>
                <span className="hint">
                  {b.paidAt
                    ? tf('tx_marked_paid', {
                      who: b.paidBy === side ? t('tx_you') : shortName(otherName),
                      d: fmtDate(b.paidAt.slice(0, 10), { long: true }),
                    })
                    : side === 'house'
                      ? t('tx_tick_paid_home')
                      : t('tx_tick_paid_cleaner')}
                </span>
              </div>
              <Switch on={!!b.paidAt} onClick={() => setPaid(b.id, !b.paidAt)} />
            </div>
          </>
        )}
      </Card>

      {(b.startedAt || b.jobState.length > 0) && (
        <Card>
          <SectionHead
            title={t(b.status === 'completed' ? 'h_whatwasdone' : 'h_progress')}
            action={`${doneCount}/${b.jobIds.length}`}
          />
          {b.jobIds.map((jobId) => {
            const st = b.jobState.find((j) => j.jobId === jobId);
            return (
              <div className="row top" key={jobId} style={{ gap: 9 }}>
                <span className={'tickbox small' + (st?.done ? ' on' : '')} aria-hidden="true">
                  {st?.done ? '✓' : ''}
                </span>
                <div className="col grow">
                  <span style={{
                    fontSize: 14,
                    color: st?.done ? 'var(--muted)' : 'var(--ink)',
                    textDecoration: st?.done ? 'line-through' : 'none',
                  }}>
                    {jobLabel(jobId)}
                  </span>
                  {st?.note && (
                    <span className="sub" style={{ color: 'var(--bergamot-deep)' }}>
                      {st.note}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {notesBack.length > 0 && side === 'house' && (
            <Note warm>
              {tf(notesBack.length === 1 ? 'tx_left_note_1' : 'tx_left_note_n', { name: shortName(c.name) })}
            </Note>
          )}
        </Card>
      )}

      <Card>
        <SectionHead title={t('h_agreedlist')} />
        <div className="chips"><JobChips ids={b.jobIds} /></div>
        {(() => {
          /* The home's own notes matter most right before somebody walks in,
             so they belong here, not only on the original request. */
          const notes = h.jobList.filter((j) => b.jobIds.includes(j.jobId) && j.note);
          if (!notes.length) return null;
          return (
            <>
              <Divider />
              <div className="col" style={{ gap: 8 }}>
                {notes.map((j) => (
                  <div className="col" key={j.jobId}>
                    <span
                      className="sub tight"
                      style={{ fontWeight: 700, color: 'var(--ink-2)' }}
                    >
                      {jobLabel(j.jobId)}
                    </span>
                    <span className="sub">{j.note}</span>
                  </div>
                ))}
              </div>
            </>
          );
        })()}
        <span className="hint">{t('b_record')}</span>
      </Card>

      {b.status === 'cancelled' ? (
        <Note>
          <b>{t('tx_cancelled')}.</b> {tf('tx_visit_off', { name: shortName(otherName) })}
        </Note>
      ) : b.status === 'confirmed' ? (
        <>
          {side === 'cleaner' ? (
            b.startedAt ? (
              <button
                type="button"
                className="btn lg block"
                onClick={() => nav('c_inprogress', { id: b.id })}
              >
                {tf('tx_back_to_job', { n: doneCount, m: b.jobIds.length })}
              </button>
            ) : b.date > TODAY_ISO ? (
              /* Not yet. The server refuses too -- this just avoids offering a
                 button that cannot work. */
              <Note>
                <b>{tf('tx_not_until', { d: fmtDate(b.date, { long: true }) })}</b>{' '}
                {t('tx_start_on_day')}
              </Note>
            ) : (
              <>
                <button
                  type="button"
                  className="btn lg block"
                  onClick={() => startBooking(b.id)}
                >
                  {t('b_arrived')}
                </button>
                <p className="hint" style={{ textAlign: 'center' }}>
                  {t('b_openslist')}
                </p>
              </>
            )
          ) : b.startedAt ? (
            <Note>
              <b>{tf('tx_has_started', { name: shortName(c.name) })}</b>{' '}
              {tf('tx_ticked_so_far', { n: doneCount, m: b.jobIds.length })}
            </Note>
          ) : null}

          <button
            type="button"
            className="btn ghost block"
            onClick={() => openChat({ houseId: h.id, cleanerId: c.id })}
          >
            {t('b_message')} {shortName(otherName)}
          </button>
          {side === 'house' && (
            <>
              {/* "Only" is doing work that has to be true. On a repeating
                  job it separates this Tuesday from every Tuesday, which is
                  the whole point. On a one-off there is no series to be
                  distinguished from, so the word says a difference that does
                  not exist -- and reads like a warning about something you
                  are not doing. */}
              <button
                type="button"
                className="btn quiet block"
                onClick={() => openSheet({ name: 'once', id: b.id })}
              >
                {b.recurringId ? t('tx_change_visit_only') : t('tx_change_visit')}
              </button>
              {/* Not before the day: a visit a week out could be marked
                  finished and reviewed on the spot. The server refuses too. */}
              {b.date <= TODAY_ISO && (
                <button
                  type="button"
                  className="btn quiet block"
                  onClick={() => completeBooking(b.id)}
                >
                  {t('b_finish')}
                </button>
              )}
            </>
          )}
          {/* This only ever showed a toast saying the other side had been
              told; nothing was sent. Now it cancels -- after a second tap,
              because it cannot be undone. */}
          {confirmCancel ? (
            <div className="col" style={{ gap: 8 }}>
              <span className="hint">
                {tf(b.recurringId ? 'tx_cancel_visit_q' : 'tx_cancel_booking_q', { name: shortName(otherName) })}
              </span>
              <button type="button" className="btn danger block" onClick={() => cancelBooking(b.id)}>
                {t('tx_yes_cancel')}
              </button>
              <button type="button" className="btn quiet block" onClick={() => setConfirmCancel(false)}>
                {t('tx_keep_it')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn danger block"
              onClick={() => setConfirmCancel(true)}
            >
              {t('b_cancelbooking')}
            </button>
          )}
        </>
      ) : (
        <>
          <Card>
            <SectionHead title={t('h_reviews')} />
            {myReview ? (
              <div className="col">
                <span className="sub tight">{t('b_youwrote')}</span>
                <Stars value={myReview.overall} />
                <span className="sub">&quot;{myReview.text}&quot;</span>
              </div>
            ) : (
              <>
                <Note warm>
                  {t('b_hidden14')}
                </Note>
                <button
                  type="button"
                  className="btn block"
                  onClick={() => nav('review', { id: b.id })}
                >
                  {t('t_review')}
                </button>
              </>
            )}
            {myReview && theirReview && (
              <>
                <Divider />
                <div className="col">
                  <span className="sub tight">{tf('tx_x_wrote', { name: otherName })}</span>
                  <Stars value={theirReview.overall} />
                  <span className="sub">&quot;{theirReview.text}&quot;</span>
                </div>
              </>
            )}
            {myReview && !theirReview && (
              <>
                <Divider />
                <span className="sub">{tf('tx_waiting_on', { name: shortName(otherName) })}</span>
              </>
            )}
          </Card>
          <button type="button" className="btn ghost block" onClick={() => rebook(b.id)}>
            {t('b_again')}
          </button>
        </>
      )}
    </div>
  );
}

export function CleanerBookingDetail() { return <BookingDetail side="cleaner" />; }
export function HouseBookingDetail() { return <BookingDetail side="house" />; }
